import React, { KeyboardEventHandler, ReactElement, useCallback, useRef, useState } from 'react'
import CreatableSelect from 'react-select/creatable'
import { components } from 'react-select'
import { Box, useThemeUI } from 'theme-ui'
import { Setter } from 'utils'
import { ColorModesScale } from '@theme-ui/css/src/types'
import { toUnicode } from 'utils/punycode'
import {
  isCountryCodeOrASNSpecialRule,
  sanitizeUrls,
} from 'components/Dashboard/Profiles/CustomRules/RuleOrFolderDialog/RuleContent'
import { Flex } from '@theme-ui/components'
import { deviceNameMask } from 'components/Dashboard/Devices/DeviceModalDialog/AddOrEditDevice/AddOrEditDevice/helpers'
import { isValidEmail } from 'utils/isValidEmail'
import ExternalSvgIcon, { IconType } from 'components/Dashboard/CommonComponents/ExternalSvgIcon'
import uniqBy from 'lodash/uniqBy'

export interface Option {
  readonly label: string
  readonly value: string
}

const createOption = (label: string, type: { isCustomRule?: boolean; isDevice?: boolean }) => {
  if (type?.isCustomRule) {
    label = sanitizeUrls(isCountryCodeOrASNSpecialRule(label) ? label : toUnicode(label))
  }

  if (type?.isDevice) {
    label = deviceNameMask(label)
  }

  return {
    label,
    value: label,
  }
}

const getValuesFromClipboardAndCreateOptions = (
  clipboardData,
  isCustomRule,
  shouldValidateEmail: boolean,
) => {
  return (clipboardData?.getData('text') || '')
    .split(/[\n,]+/)
    .map((value: string) => {
      if ((shouldValidateEmail && !isValidEmail(value)) || !value) {
        return
      }
      return createOption(value, isCustomRule)
    })
    .filter(Boolean)
}

const getCustomStyles = (
  themeColors?: ColorModesScale,
  isError?: boolean,
  isSmallInput?: boolean,
  isInputEmpty?: boolean,
) => {
  return {
    control: (_, state) => ({
      position: 'relative',
      outline: 'none',
      backgroundColor: themeColors?.blue,
      borderRadius: '0.8rem',
      padding: isSmallInput ? '0.2rem 1.2rem' : '0.8rem 1.2rem',
      paddingRight: '0.4rem',
      border: '1px solid',
      borderColor: isError
        ? themeColors?.errorOpaque
        : state.isFocused
        ? themeColors?.cyan800
        : themeColors?.blueYonder40,
      '&:hover': {
        borderColor: isError
          ? themeColors?.errorOpaque
          : !state.isFocused
          ? themeColors?.blueYonder80
          : 'none',
      },
    }),
    indicatorsContainer: styles => ({
      ...styles,
      display: 'none',
    }),
    valueContainer: styles => ({
      ...styles,
      padding: 0,
      paddingRight: '0.8rem',
      maxHeight: isSmallInput ? '6.8rem' : '10rem',
      minHeight: isSmallInput && '3.2rem',
      overflow: 'auto',
      '&::-webkit-scrollbar': {
        position: 'absolute',
        width: '5px',
        backgroundColor: '#222',
      },

      '&::-webkit-scrollbar-thumb': {
        borderRadius: '3.5px',
        backgroundColor: '#444',
      },
    }),
    multiValue: styles => ({
      ...styles,
      backgroundColor: themeColors?.white6,
      padding: '0.2rem 0.8rem',
      borderRadius: '45px',
    }),
    multiValueLabel: styles => ({
      ...styles,
      fontSize: '1.2rem',
      fontWeight: 400,
      padding: 0,
      paddingRight: '0.4rem',
      color: themeColors?.aliceBlue60,
    }),
    multiValueRemove: styles => ({
      ...styles,
      padding: 0,
      backgroundColor: 'transparent',
      borderRadius: '50%',
      color: themeColors?.aliceBlue60,
      ':hover': {
        color: themeColors?.aliceBlue,
        cursor: 'pointer',
      },
      '& > svg': {
        width: '1.6rem',
        height: '1.6rem',
      },
    }),
    placeholder: styles => ({
      ...styles,
      padding: '0.2rem',
      fontSize: '1.4rem',
      lineHeight: '138%',
      letterSpacing: '0.56px',
      color: themeColors?.aliceBlue40,
      /*
      In the library (be default) it is not an input placeholder but just a div with text above another div,
       so that css props allow to resolve an issue with the selecting text on Android devices
       */
      userSelect: 'none',
      webkitUserSelect: 'none',
      mozUserSelect: 'none',
      msUserSelect: 'none',
      webkitTouchCallout: 'none',
      cursor: 'none',
      pointerEvents: 'none',
      zIndex: 0,
    }),
    input: styles => ({
      ...styles,
      padding: isSmallInput ? 0 : '0.2rem',
      margin: isSmallInput ? 0 : 'unset',
      color: themeColors?.aliceBlue,
      zIndex: 5,
      /*
      The input in the library (be default) takes up only 2px, everything else is just an area to fill with multiple elements.
        But we want the entire area to work as an input, especially on Android devices when there is no content
         and the user tries to paste something. That solution helps to display the context menu correct
          (especially on Android devices) and make the whole area an input when there is no content there.
       */
      ...(isInputEmpty
        ? {
            width: '100%',
            '& > div': {
              width: '100%',
              '& > input': {
                width: '100% !important',
              },
            },
          }
        : {}),
    }),
    menu: styles => ({
      ...styles,
      backgroundColor: themeColors?.raisinBlack,
      borderRadius: '1.2rem',
      border: `1px solid ${themeColors?.blueYonder30}`,
      boxShadow: '0 4px 8px 0 rgba(0, 0, 0, 0.06)',
      padding: '0 0.4rem',
    }),
    menuList: styles => ({
      ...styles,
      display: 'flex',
      flexDirection: 'column',
      paddingLeft: 0,
      paddingRight: 0,
      gap: '0.1rem',
    }),
    option: styles => ({
      ...styles,
      fontSize: '1.4rem',
      cursor: 'pointer',
      padding: '0.8rem 0.4rem',
      color: themeColors?.aliceBlue60,
      backgroundColor: 'transparent',
      borderRadius: '0.8rem',
      '&:hover': {
        color: themeColors?.white,
        backgroundColor: themeColors?.littleBoyBlue5,
      },
      '&:active': {
        backgroundColor: 'transparent',
      },
    }),
  }
}

const MultiValue = ({ children, ...props }) => {
  return (
    <components.MultiValue {...props}>
      <Flex sx={{ alignItems: 'center', gap: '0.8rem' }}>
        {isCountryCodeOrASNSpecialRule(children) && (
          <ExternalSvgIcon
            icon={children.replace(/^!|@|#/g, '')}
            defaultColor="aliceBlue60"
            type={IconType.LOCATIONS}
            sx={{ width: '1.6rem', height: '1.6rem' }}
            descriptiveText={`${children.replace(/^!|@|#/g, '')} location icon`}
          />
        )}
        {children}
      </Flex>
    </components.MultiValue>
  )
}

const unidentifiedEventKey = 'Unidentified'

export default function MultiSelectInput({
  placeholder,
  value,
  setValue,
  testId,
  ariaLabel,
  isError,
  isSmallInput,
  isCustomRule,
  isDevice,
  isDisabled = false,
  onInputChange,
  onChange,
  onBlurChange,
  options,
  menuIsOpen,
  shouldValidateEmail = false,
  shouldAllowPaste,
}: {
  placeholder?: string
  value?: Option[] | undefined
  setValue?: Setter<Option[] | undefined>
  testId?: string
  ariaLabel?: string
  isError?: boolean
  isSmallInput?: boolean
  isCustomRule?: boolean
  isDevice?: boolean
  isDisabled?: boolean
  onInputChange?: (newValue?: string) => void
  onChange?: (value: Option[] | undefined | null) => void
  onBlurChange?: (value?: Option[]) => void
  options?: Option[]
  menuIsOpen?: boolean
  shouldValidateEmail?: boolean
  shouldAllowPaste?: boolean
}): ReactElement {
  const [inputValue, setInputValue] = useState('')
  const { theme } = useThemeUI()
  const selectRef = useRef<HTMLSelectElement>(null)
  const eventKeyRef = useRef<string>('')
  const isBlurEventRef = useRef(false)

  const createOptionFromInputValue = useCallback(
    (currentInputValue: string) => {
      setValue?.(prev => {
        if (
          (shouldValidateEmail && !isValidEmail(currentInputValue)) ||
          prev?.some(item => item.value === currentInputValue)
        ) {
          return prev
        }

        if (prev) {
          return [...prev, createOption(currentInputValue, { isCustomRule, isDevice })]
        }

        return [createOption(currentInputValue, { isCustomRule, isDevice })]
      })

      setInputValue('')
    },
    [isCustomRule, isDevice, setValue, shouldValidateEmail],
  )

  const handleOptionCreation = useCallback(
    event => {
      event.preventDefault()

      if (event.type === 'paste') {
        if (
          isDisabled ||
          (!shouldAllowPaste && !isCustomRule && event.target.ariaLabel !== ariaLabel)
        ) {
          return
        }

        const options = getValuesFromClipboardAndCreateOptions(
          event.clipboardData,
          isCustomRule,
          shouldValidateEmail,
        )

        // remove empty options
        const filteredOptions = options?.filter(option => !!option.value?.trim())

        // remove duplicates
        const uniqOptions = uniqBy(filteredOptions as Option[], option => option.value?.trim())

        onBlurChange?.(uniqOptions)

        selectRef.current?.focus()

        return setValue?.(prev => {
          if (prev) {
            return [...prev, ...uniqOptions]
          }

          return [...uniqOptions]
        })
      }

      createOptionFromInputValue(inputValue)
    },
    [
      createOptionFromInputValue,
      inputValue,
      isDisabled,
      shouldAllowPaste,
      isCustomRule,
      ariaLabel,
      shouldValidateEmail,
      onBlurChange,
      setValue,
    ],
  )

  const handleKeyDown: KeyboardEventHandler = event => {
    if (!inputValue) {
      // allow backspace to edit last option
      if (event.key === 'Backspace') {
        const lastOption = value?.[value.length - 1]
        if (lastOption) {
          setValue?.(value.slice(0, -1))
        }
        setInputValue(lastOption?.value || '')
        onInputChange?.(lastOption?.value || '')
      }
      return
    }

    if (event.type === 'blur') {
      handleOptionCreation(event)
    }

    switch (event.key) {
      case 'Enter':
      case 'Tab':
      case ',':
        handleOptionCreation(event)
    }
  }

  const customStyles = getCustomStyles(theme.colors, isError, isSmallInput, !value?.length)

  return (
    <Box
      data-testid={'paste-listener'}
      onPaste={handleOptionCreation}
      sx={{
        ':focus-visible': {
          outline: 'none',
        },
      }}
    >
      <CreatableSelect
        ref={selectRef}
        aria-label={ariaLabel}
        data-testid={testId}
        styles={customStyles}
        components={{ MultiValue }}
        inputValue={inputValue || ''}
        isClearable
        isMulti
        focus={true}
        isDisabled={isDisabled}
        menuIsOpen={menuIsOpen ? undefined : false}
        onChange={(newValue, event) => {
          if (event.action !== 'pop-value') {
            setValue?.(newValue)
            onChange?.(newValue)
          }
        }}
        onInputChange={newValue => {
          setInputValue(newValue)

          // should not call onInputChange if onBlur event, otherwise a conflict between event handlers occurs
          if (!newValue && isBlurEventRef.current) {
            return
          }

          isBlurEventRef.current = false
          onInputChange?.(newValue)

          // event key can be Unidentified for Android devices,
          // in that case we have to create an option based on the input value
          if (
            eventKeyRef.current === unidentifiedEventKey &&
            newValue.length > 2 &&
            newValue.endsWith(',')
          ) {
            createOptionFromInputValue(newValue.split(',')[0])
          }
        }}
        onBlur={e => {
          const targetValue = e.target.value

          isBlurEventRef.current = true
          handleKeyDown(e)

          onBlurChange?.([...(value || []), { label: targetValue, value: targetValue }])
        }}
        onKeyDown={event => {
          if (event.key !== unidentifiedEventKey) {
            handleKeyDown(event)
          }

          eventKeyRef.current = event.key
        }}
        placeholder={placeholder || 'Type something and press enter...'}
        value={value || ''}
        options={options}
        noOptionsMessage={() => null}
        isValidNewOption={(inputValue: string) => {
          if (!shouldValidateEmail) {
            return true
          }

          return isValidEmail(inputValue)
        }}
      />
    </Box>
  )
}
